<template>
  <div class="input-wrap">
    <label class="input-label" v-if="label" :for="uniqueId" v-bind="$attrs">{{
      label
    }}</label>
    <input
      :id="uniqueId"
      class="input-main"
      :class="{ 'input-error': error }"
      v-if="type === 'text'"
      type="text"
      :value="modelValue"
      :placeholder="error && showInputError ? errorMessage : placeholder"
      @input="$emit('update:modelValue', $event.target.value)"
      :aria-invalid="error"
      :aria-describedby="
        error && errorMessage && !showInputError ? uniqueId + '-error' : null
      "
      v-bind="$attrs"
    />
    <div v-if="type === 'password'" style="position: relative">
      <input
        :id="uniqueId"
        class="input-main"
        :class="{ 'input-error': error }"
        :type="isPasswordVisible ? 'text' : 'password'"
        :value="modelValue"
        :placeholder="error && showInputError ? errorMessage : placeholder"
        @input="$emit('update:modelValue', $event.target.value)"
        :aria-invalid="error"
        :aria-describedby="
          error && errorMessage && !showInputError ? uniqueId + '-error' : null
        "
        v-bind="$attrs"
      />

      <span
        @click="togglePasswordVisibility"
        role="button"
        tabindex="0"
        aria-label="Toggle Password Visibility"
        @keydown.enter="togglePasswordVisibility"
        @keydown.space.prevent="togglePasswordVisibility"
        style="
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          margin-top: 5px;
          margin-right: 3px;
        "
      >
        <img
          v-if="isPasswordVisible"
          src="/images/eye.svg"
          alt=""
          aria-hidden="true"
          width="20"
          height="20"
        />

        <img
          v-else
          src="/images/reveal-password.svg"
          alt=""
          aria-hidden="true"
          width="20"
          height="20"
        />
      </span>
    </div>

    <p
      v-if="error && errorMessage && !showInputError"
      :id="uniqueId + '-error'"
      style="
        color: var(--error-red);
        margin-top: 5px;
        font-size: var(--fs-small);
        margin-bottom: 0px;
      "
    >
      {{ errorMessage }}
    </p>

    <textarea
      :id="uniqueId"
      :class="{ 'input-main': true, 'input-error': error }"
      v-else-if="type === 'multiline'"
      :value="modelValue"
      :placeholder="error ? errorMessage : placeholder"
      @input="$emit('update:modelValue', $event.target.value)"
      :aria-invalid="error"
      :aria-describedby="error && errorMessage ? uniqueId + '-error' : null"
      v-bind="$attrs"
    ></textarea>
    <div v-if="type === 'select'" class="dropdown" ref="dropdownRef">
      <input
        :id="uniqueId"
        class="input-main"
        :class="{ 'input-error': error }"
        type="text"
        style="width: 100%"
        :value="selectedOptionText"
        :placeholder="error && showInputError ? errorMessage : placeholder"
        readonly
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="showDropdown"
        :aria-invalid="error"
        :aria-describedby="
          error && errorMessage && !showInputError ? uniqueId + '-error' : null
        "
        v-bind="$attrs"
        @click="toggleDropdown"
      />
      <img
        :src="currentIcon"
        class="dropdown-icon"
        :class="{ 'rotate-icon': showDropdown }"
        @click="iconClickHandler"
        alt="Toggle dropdown"
        role="button"
        tabindex="0"
        @keydown.enter="iconClickHandler"
        @keydown.space.prevent="iconClickHandler"
      />
      <Teleport to="body">
        <div
          v-if="showDropdown"
          class="dropdown-panel"
          :style="getDropdownStyles()"
          ref="dropdownPanelRef"
          @click.stop
        >
          <input
            v-if="showSearch"
            class="input-search"
            type="text"
            placeholder="Search ..."
            @input="searchHandler($event.target.value)"
            @keyup.stop="
              apiSearchEnabled && debounceApiSearch($event.target.value)
            "
            @click.stop
          />
          <div
            class="options-container"
            :class="{ 'no-search': !showSearch }"
            @click.stop
          >
            <div v-if="isLoading" class="loading-indicator">
              <div class="input-spinner"></div>
            </div>
            <template v-else>
              <div
                v-for="(option, index) in filteredOptions"
                :key="index"
                @click.stop="selectOption(option)"
              >
                {{ option.text }}
              </div>
              <div v-if="filteredOptions.length === 0" class="no-results">
                No results found
              </div>
            </template>
          </div>
        </div>
      </Teleport>
    </div>
    <div v-if="type === 'multipleSelect'" class="dropdown" ref="dropdownRef">
      <input
        :id="uniqueId"
        class="input-main"
        type="text"
        style="width: 100%"
        :value="selectedItemsCount"
        :placeholder="placeholder"
        readonly
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="showDropdown"
        v-bind="$attrs"
        @click="toggleDropdown"
      />
      <button
        type="button"
        aria-label="Toggle dropdown"
        @click="iconClickHandler"
      >
        <img
          :src="currentIcon"
          class="dropdown-icon"
          :class="{ 'rotate-icon': showDropdown }"
          alt=""
          aria-hidden="true"
        />
      </button>

      <Teleport to="body">
        <div
          v-if="showDropdown"
          class="dropdown-panel"
          :style="getDropdownStyles()"
          ref="dropdownPanelRef"
          @click.stop
        >
          <input
            v-if="showSearch"
            class="input-search"
            type="text"
            placeholder="Search ..."
            @input="searchHandler($event.target.value)"
            @keyup.stop="
              apiSearchEnabled && debounceApiSearch($event.target.value)
            "
            @click.stop
          />

          <div
            class="options-container"
            :class="{ 'no-search': !showSearch }"
            @click.stop
          >
            <div v-if="isLoading" class="loading-indicator">
              <div class="input-spinner"></div>
            </div>
            <template v-else>
              <div
                v-for="(option, index) in filteredOptions"
                :key="index"
                class="checkbox-option"
                @click.stop
              >
                <input
                  type="checkbox"
                  :id="'checkbox-' + index"
                  :value="option.value"
                  :checked="isSelected(option.value)"
                  @change.stop="toggleSelection(option.value, index)"
                  @click.stop
                  :disabled="shouldDisableCheckbox(option.value)"
                  v-model="selectedValues"
                />
                <label :for="'checkbox-' + index" @click.stop>{{
                  option.text
                }}</label>
              </div>
              <div v-if="filteredOptions.length === 0" class="no-results">
                No results found
              </div>
            </template>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  onMounted,
  onUnmounted,
  computed,
  nextTick,
  getCurrentInstance,
} from "vue";
import { watch, watchEffect } from "vue";
import { debounce } from "lodash";

const props = defineProps({
  label: {
    type: [String, Boolean],
    default: false,
  },
  modelValue: {
    type: [String, Array, Number],
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  options: {
    type: Array,
    default: () => [],
  },
  error: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  maxHeight: {
    type: String,
    default: "",
  },
  apiSearchEnabled: {
    type: Boolean,
    default: false,
  },
  apiSearchFunction: {
    type: Function,
    default: null,
  },
  apiSearchDebounce: {
    type: Number,
    default: 300,
  },
  showInputError: {
    type: Boolean,
    default: true,
  },
  maximumMultipleSelect: {
    type: Number,
    default: 2,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  id: {
    type: String,
    default: "",
  },
});

const instance = getCurrentInstance();
const uniqueId = computed(
  () =>
    props.id ||
    `base-input-${instance?.uid || Math.random().toString(36).substring(2, 9)}`
);

const isPasswordVisible = ref(false);
const currentIcon = ref("/images/arrow-circle-left.svg");
const dropdownPosition = ref({ top: 0, left: 0, width: 0 });
let positionObserver = null;
const dropdownRef = ref(null);
const dropdownPanelRef = ref(null);

const dropdownMaxHeight = computed(() => {
  return props.maxHeight ? `${props.maxHeight}px` : "none";
});

const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value;
};

onMounted(() => {
  window.addEventListener("click", closeDropdown);
  window.addEventListener("scroll", handleScroll, true);
  window.addEventListener("resize", handleResize);

  if (
    (props.type === "select" && props.modelValue) ||
    (props.type === "multipleSelect" &&
      props.modelValue &&
      props.modelValue.length > 0)
  ) {
    currentIcon.value = "/images/close.svg";
  } else {
    currentIcon.value = "/images/arrow-circle-left.svg";
  }
});

onUnmounted(() => {
  window.removeEventListener("click", closeDropdown);
  window.removeEventListener("scroll", handleScroll, true);
  window.removeEventListener("resize", handleResize);
  if (positionObserver) {
    positionObserver.disconnect();
    positionObserver = null;
  }
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      currentIcon.value = "/images/arrow-circle-left.svg";
    }
  }
);

watchEffect(() => {
  if (
    (props.type === "select" && props.modelValue) ||
    (props.type === "multipleSelect" &&
      props.modelValue &&
      props.modelValue.length > 0)
  ) {
    currentIcon.value = "/images/close.svg";
  } else {
    currentIcon.value = "/images/arrow-circle-left.svg";
  }
});

const emit = defineEmits(["update:modelValue", "update:options"]);

const searchText = ref("");
const showDropdown = ref(false);

const updateDropdownPosition = () => {
  if (!dropdownRef.value || !showDropdown.value || !dropdownPanelRef.value)
    return;

  const rect = dropdownRef.value.getBoundingClientRect();
  const dropdownPanelRect = dropdownPanelRef.value.getBoundingClientRect();

  // Calculate position relative to viewport
  const viewportTop = rect.bottom;
  const viewportLeft = rect.left;

  // Account for scrolling
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

  // Final position with scroll offset
  dropdownPosition.value = {
    top: viewportTop + scrollTop,
    left: viewportLeft + scrollLeft,
    width: rect.width,
  };

  // Check if dropdown would appear off-screen and adjust if needed
  const viewportHeight = window.innerHeight;
  const dropdownHeight = dropdownPanelRect.height;

  if (viewportTop + dropdownHeight > viewportHeight) {
    // Position dropdown above the input if it would go off bottom of screen
    dropdownPosition.value.top = scrollTop + rect.top - dropdownHeight;
  }
};

const getDropdownStyles = () => {
  return {
    "--dropdown-max-height": dropdownMaxHeight.value,
    position: "absolute",
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
    "z-index": "9999",
  };
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;

  if (showDropdown.value) {
    resetSearch();
    nextTick(() => {
      updateDropdownPosition();

      // Add MutationObserver to detect DOM changes that might affect positioning
      if (!positionObserver) {
        positionObserver = new MutationObserver(() => {
          if (showDropdown.value) {
            window.requestAnimationFrame(() => {
              updateDropdownPosition();
            });
          }
        });
        positionObserver.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    });
  } else {
    resetSearch();
    if (positionObserver) {
      positionObserver.disconnect();
      positionObserver = null;
    }
  }
};

const iconClickHandler = () => {
  if (
    (selectedOptionText.value || selectedItemsCount) &&
    currentIcon.value.includes("close.svg")
  ) {
    clearSelection();
  } else {
    toggleDropdown();
  }
};

const closeDropdown = (event) => {
  // Don't close if click is inside the dropdown or its panel
  if (dropdownRef.value && dropdownRef.value.contains(event.target)) {
    return;
  }

  // Also check if click is inside the teleported dropdown panel
  if (dropdownPanelRef.value && dropdownPanelRef.value.contains(event.target)) {
    return;
  }

  showDropdown.value = false;
  searchText.value = "";

  if (props.apiSearchEnabled && searchText.value === "") {
    if (props.type === "multipleSelect" || props.type === "select") {
      // performApiSearch("");
    }
  }
};

const clearSelection = () => {
  selectedOptionText.value = "";

  if (props.type === "multipleSelect") {
    selectedValues.value = [];
    emit("update:modelValue", []);
  } else {
    emit("update:modelValue", "");
  }

  currentIcon.value = "/images/arrow-circle-left.svg";
  showDropdown.value = false;
};

const selectOption = (option) => {
  // Stop event propagation to prevent unwanted closing
  currentIcon.value = "/images/close.svg";
  selectedOptionText.value = option.text;
  emit("update:modelValue", option.value);
  searchText.value = "";

  // Manually close the dropdown after option selection
  // Use nextTick to ensure the selection is processed before closing
  nextTick(() => {
    showDropdown.value = false;
  });
};

const isLoading = ref(false);
let debounceTimeout = null;

const filteredOptions = computed(() => {
  if (props.apiSearchEnabled) {
    return props.options;
  }

  if (!searchText.value || !props.showSearch) {
    return props.options;
  }

  return props.options.filter((option) =>
    option.text.toLowerCase().includes(searchText.value.toLowerCase())
  );
});

const searchHandler = (value) => {
  searchText.value = value;

  if (!props.apiSearchEnabled) return;
};

const debounceApiSearch = (value) => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  if (!value || value.length === 0) {
    performApiSearch("");
    return;
  }

  debounceTimeout = setTimeout(() => {
    performApiSearch(value);
  }, props.apiSearchDebounce);
};

const performApiSearch = async (query) => {
  if (!props.apiSearchFunction) return;

  try {
    isLoading.value = true;
    const results = await props.apiSearchFunction(query);
    emit("update:options", results);
  } catch (error) {
    console.error("Error performing API search:", error);
  } finally {
    isLoading.value = false;
  }
};

const resetSearch = () => {
  searchText.value = "";
  if (props.apiSearchEnabled && props.apiSearchFunction) {
    performApiSearch("");
  }
};

const selectedOptionText = computed(() => {
  if (props.type === "select") {
    const option = props.options.find((o) => o.value === props.modelValue);
    return option ? option.text : "";
  }
  return "";
});

const isSelected = computed(() => {
  return (optionValue) => props.modelValue.includes(optionValue);
});

const toggleSelection = (optionValue) => {
  // Stop event propagation to prevent unwanted closing
  const newValue = [...props.modelValue];
  const index = newValue.indexOf(optionValue);
  if (index === -1) {
    if (
      newValue.length < props.maximumMultipleSelect ||
      props.maximumMultipleSelect < 0
    ) {
      newValue.push(optionValue);
    } else {
      alert("You can only select up to 2 items.");
      return;
    }
  } else {
    newValue.splice(index, 1);
  }
  emit("update:modelValue", newValue);

  // Don't close dropdown after selection in multiselect
};

const selectedValues = ref([]);

// Add this watch to sync selectedValues with modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    if (props.type === "multipleSelect" && Array.isArray(newValue)) {
      selectedValues.value = [...newValue];
    }
  },
  { immediate: true }
);

const shouldDisableCheckbox = (checkboxId) => {
  let maxSelectedCondition = false;
  if (props.maximumMultipleSelect >= 0) {
    maxSelectedCondition =
      selectedValues.value.length >= props.maximumMultipleSelect;
  }

  return maxSelectedCondition && !selectedValues.value.includes(checkboxId);
};

const selectedItemsCount = computed(() => {
  const count = props.modelValue.length;
  return count === 0 ? "" : `${count} Selected`;
});

// Debounce resize events to improve performance
const handleResize = debounce(() => {
  if (showDropdown.value) {
    updateDropdownPosition();
  }
}, 100);

// Handle scroll events with throttling for better performance
const handleScroll = () => {
  if (showDropdown.value) {
    window.requestAnimationFrame(() => {
      updateDropdownPosition();
    });
  }
};
</script>

<style scoped>
.input-wrap {
  display: flex;
  flex-direction: column;

  input {
    padding: 8px 12px;
    font-size: var(--fs-medium);
  }
}

.dropdown {
  position: relative;
}

.dropdown input {
  cursor: pointer;
}

.btn-main {
  border-radius: 25px;
  text-decoration: none;
  font-family: "League Spartan";
  font-weight: 500;
  background-color: #00925e;
  color: white;
  display: block;
  padding: 0.5em;
}

.btn-main:hover {
  background-color: #00734a;
}

input.input-main,
textarea.input-main,
select.input-main {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-size: var(--fs-big);
  font-family: "League Spartan", sans-serif;
}

input.input-main::placeholder,
textarea.input-main::placeholder,
select.input-main::placeholder {
  color: #707070;
}

input.input-main:focus,
textarea.input-main:focus,
select.input-main:focus {
  border: 0.5px solid var(--primary-blue) !important;
}

textarea.input-main {
  height: 100%;
  padding: 5px 10px;
}

select.input-main {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
}

.input-label {
  color: #3b4648;
  font-family: "League Spartan";
  font-weight: 400;
  margin-bottom: 4px;
}

.dropdown-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.3s;
}

.rotate-icon {
  transform: translateY(-50%) rotate(180deg);
  transition: transform 0.3s;
}

.input-search {
  position: relative;
  width: calc(100% - 10px);
  padding: 8px 10px;
  z-index: 10000;
  background-color: #fff;
  border-radius: 10px;
  border: 1px solid var(--border-color) !important;
  min-width: 200px;
  margin: 5px;
}

.dropdown-panel {
  font-family: "League Spartan", sans-serif;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 9999;
  pointer-events: auto;
}

.options-container {
  -ms-overflow-style: none;
  background-color: #f2f3fb;
  border: 1px solid var(--border-color) !important;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  max-height: var(--dropdown-max-height);
  overflow-y: auto;
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  min-width: 200px;
  width: 100%;
  z-index: 9999;
  padding: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.options-container.no-search {
  top: 5px;
}

/* Updated scrollbar styles */
.options-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
  margin: 5px;
}

.options-container::-webkit-scrollbar-thumb {
  background: rgba(0, 61, 106, 0.2);
  border-radius: 10px;
  transition: background 0.2s ease;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 61, 106, 0.4);
}

/* For Firefox */
.options-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 61, 106, 0.2) transparent;
}

.options-container div:hover {
  background-color: #f0f0f0;
}

.options-container div {
  color: var(--primary-blue);
  padding: 8px 12px;
  cursor: pointer;
}

.options-container div:not(:first-child) {
  border-top: 1px solid var(--border-color);
}

.options-container div:first-child:hover {
  border-radius: 10px 10px 0 0;
}

.options-container div:last-child:hover {
  border-radius: 10px 10px;
}

.checkbox-option {
  display: flex;
  align-items: center;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
}

.input-error {
  border: 1px solid var(--error-red) !important;
}

.input-error .custom-input {
  border: none !important;
}

/* Existing styles for input, textarea, and select elements */
input.input-main,
textarea.input-main,
select.input-main {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  font-size: var(--fs-big);
  font-family: "League Spartan", sans-serif;
}

/* Add hover styles for input, textarea, and select elements */
input.input-main:hover,
textarea.input-main:hover,
select.input-main:hover {
  border-color: var(--neutral-700);
  /* Change border color to red */
  background-color: #eaecff;
  /* Optional: change background color to a lighter red for better visibility */
}

/* Maintain focus style */
input.input-main:focus,
textarea.input-main:focus,
select.input-main:focus {
  border: 1px solid var(--primary-blue) !important;
}

input.input-main:disabled,
textarea.input-main:disabled,
select.input-main:disabled {
  background-color: #f1f2f3;
  color: var(--neutral-700);
}

.input-wrap {
  display: flex;
  flex-direction: column;
}

input.input-main,
textarea.input-main,
select.input-main {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: var(--fs-big);
  padding: 8px 35px 8px 8px;
  /* Normal padding */
  box-sizing: border-box;
  /* Pastikan perubahan padding tidak mengubah ukuran */
  transition:
    padding 0.3s ease,
    border-color 0.3s ease;
  /* Transisi untuk padding dan border color */
  font-family: "League Spartan", sans-serif;
}

/* Efek hover yang diperbaiki */
textarea.input-main:hover {
  padding-top: 10px;
  /* Penyesuaian padding atas */
  padding-bottom: 6px;
  /* Penyesuaian padding bawah untuk menjaga ukuran total */
  border-color: var(--neutral-700);
  /* Opsi: ubah warna border saat hover */
}

/* Fokus tanpa mengubah ukuran */
input.input-main:focus,
textarea.input-main:focus,
select.input-main:focus {
  border: 1px solid var(--primary-blue) !important;
  outline: none;
  /* Menghapus outline default */
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--primary-blue);
  width: 100%;
}

.input-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f0f0f0;
  border-top: 3px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.no-results {
  padding: 10px;
  text-align: center;
  color: #787878;
  font-style: italic;
}
</style>
